import React, { useRef, FormEvent, useState, useEffect } from "react";
import styles from "../InputForm.module.css";
import Input from "../../../Components/Input/Input";
import TextArea from "../../../Components/TextArea/TextArea";
import Button from "../../../Components/Button/Button";
import { EncryptionService } from "../../../services/EncryptionService";
import { Link, useNavigate } from "react-router-dom";
import CheckboxList, {
  CheckboxOption,
} from "../../../Components/CheckList/Checklist";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import useAPI from "../../../Hooks/useAPI";
import debounce from "lodash.debounce";
import { useUser } from "../../../Context/UserContext";

interface ContactFormData {
  companyName: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  requInfo: string; //[],
  message: string;
  ipaddress: string;
  datasource: string;
}

const InputForm: React.FC = (): JSX.Element => {
  const { user, setUser } = useUser();
  const [encryptionService] = useState(() => new EncryptionService());
  const [selected, setSelected] = useState<string[]>([]);
  const { postSendReqInfoEmail, saveContact } = useAPI();
  const [cidn, setCIDN] = useState<number | null>(null);
  const [, setEmail] = useState("");
  const [, setFirstName] = useState("");
  const [, setLastName] = useState("");
  const [, setCompanyName] = useState("");
  const [, setTitle] = useState("");

  // --- Initialize existing contact ID from localStorage ---
  useEffect(() => {
    const storedId = Number(localStorage.getItem("partial_contact_id"));
    if (storedId) {
      setCIDN(storedId);
    }
  }, []);

  const fetchIp = async () => {
    try {
      const res = await fetch("https://api.ipify.org/?format=json");
      const data = await res.json();

      setFormData((prev) => ({
        ...prev,
        ipaddress: data.ip,
      }));

      console.log("Fetched IP:", data.ip);
    } catch (err) {
      console.error("IP fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchIp();
  }, []);

  const [formData, setFormData] = useState<ContactFormData>({
    companyName: "",
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    requInfo: "", //[]
    message: "",
    ipaddress: "",
    datasource: "requestinfo",
  });

  const options: CheckboxOption[] = [
    { label: "Business Information Meeting", value: "BuisnessRM" },
    { label: "Software/Data Information Meeting", value: "SoftwareRM" },
    { label: "Engine Tech Information Meeting", value: "TechRM" },
    { label: "Protected Products Information", value: "ProductsPI" },
    { label: "Protected Investor Information", value: "InvestorPI" },
    { label: "Protected News Information", value: "NewsPI" },
  ];

  useEffect(() => {
    // 1. Always initialize with whatever user context is currently available
    if (user) {
      setFormData({
        companyName: user.companyName || "",
        title: user.title || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        requInfo: "", //[]
        message: "",
        ipaddress: "",
        datasource: "requestinfo",
      });
    }
  }, [user?.id]); // runs when user is ready

  useEffect(() => {
    encryptionService.loadPublicKey().catch((err) => {
      console.error("Failed to load public key:", err);
    });
  }, [encryptionService]);

  const form = useRef<HTMLFormElement | null>(null);

  const navigate = useNavigate();

  const sendEmail = async (e: FormEvent) => {
    e.preventDefault();

    const submitter = (e as unknown as SubmitEvent)
      .submitter as HTMLButtonElement;
    const buttonClicked = submitter?.id || submitter?.name;

    if (!form.current) return;

    const formData = new FormData(e.currentTarget as HTMLFormElement);

    const updatedFormData = {
      companyName: formData.get("companyName"),
      title: formData.get("title"),
      firstName: formData.get("firstName"),
      lastName: formData.get("lastName"),
      email: formData.get("email"),
      requInfo: selected,
      message: formData.get("text"),
    };

    console.log(formData.get("firstName"));

    // Send email
    console.log(updatedFormData);
    postSendReqInfoEmail(
      updatedFormData.email as string,
      updatedFormData.firstName as string,
      updatedFormData.lastName as string,
      updatedFormData.title as string,
      updatedFormData.requInfo as string[],
      updatedFormData.message as string,
      updatedFormData.companyName as string
    );

    await new Promise((resolve) => setTimeout(resolve, 100));
    form.current.reset();
    navigate("../sent");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
  };

  // --- Save data after tabbing off each field ---
  const debouncedSaveRef = React.useRef(
    debounce(
      async (
        updatedData: ContactFormData,
        cidn: Number | null,
        fieldname: string | null
      ) => {
        try {
          const bReturnAll = fieldname === "email"; //set breturnall to true if fieldname is email
          //console.log("payload: ", updatedData);
          const response = await saveContact(updatedData, bReturnAll, cidn);
          const out_cidn = response.out_cidn;

          //Only update all the fields in the page if we have a match by email address
          if (bReturnAll) {
            const returnedEmail = response?.email;
            const returnedFirstName = response?.firstname;
            const returnedLastName = response?.lastname;
            const returnedCompanyName = response?.companyname;
            const returnedTitle = response?.title;

            setEmail(returnedEmail);
            setFirstName(returnedFirstName);
            setLastName(returnedLastName);
            setCompanyName(returnedCompanyName);
            setTitle(returnedTitle);

            // Update the form field so the input shows it
            setFormData((prev) => ({
              ...prev,
              email: returnedEmail || "",
              firstName: returnedFirstName || "",
              lastName: returnedLastName || "",
              companyName: returnedCompanyName || "",
              title: returnedTitle || "",
            }));
          }

          // If new partial ID returned, store it
          const returnedId = out_cidn;
          if (returnedId && !cidn) {
            localStorage.setItem("partial_contact_id", returnedId);
            setCIDN(returnedId);
          }
        } catch (error) {
          console.error("Error auto-saving contact:", error);
        }
      },
      600
    ) // control debounce timing here
  ).current;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Update form state
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Send to debounced auto-save
      debouncedSaveRef(updated, cidn, name);

      return updated;
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("partial_contact_id");
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedSaveRef.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      debouncedSaveRef.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      // Component is unmounting → user left the page
      localStorage.removeItem("partial_contact_id");
    };
  }, []);

  return (
    <div>
      <form
        id="form"
        ref={form}
        className={styles.formContainer}
        onSubmit={sendEmail}
      >
        <div className={styles.formContent}>
          <Input
            label="Email"
            type="email"
            id="email"
            placeholder="Email"
            required={true}
            name="email"
            value={formData?.email ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="First Name"
            type="text"
            id="firstName"
            placeholder="First Name"
            required={true}
            name="firstName"
            value={formData?.firstName ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Last Name"
            type="text"
            id="lastName"
            placeholder="Last Name"
            required={true}
            name="lastName"
            value={formData?.lastName ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Company Name"
            type="text"
            id="companyName"
            placeholder="Company Name"
            required={false}
            name="companyName"
            value={formData?.companyName ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <Input
            label="Title"
            type="text"
            id="companyTitle"
            placeholder="Title"
            required={false}
            name="title"
            value={formData?.title ?? ""}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <CheckboxList
            options={options}
            selectedValues={selected}
            onChange={setSelected}
            label="Request Information and/or Meeting*"
          />
          {/* <MultiSelectDropdown/> */}
          <TextArea
            label="Message"
            id="message"
            placeholder="Message"
            required={true}
            rows={4}
            cols={50}
            name="text"
          />
          <div className={styles.needPermission}>
            <h4 className={styles.disclaimer}>
              We will process your request over the next few days. Some of the
              requests are based on user account. Other requests require a MNDA.
              If you have not already done so please{" "}
              <Link to="../user-login">register</Link>.
            </h4>
          </div>
        </div>
        <div className={styles.submittArea}>
          <div className={styles.buttonFormat}>
            <Button
              name="send"
              text="Send"
              type="submit"
              iconPath={faPaperPlane}
            />
          </div>
        </div>
      </form>
      <div className={styles.linkContainer}></div>
    </div>
  );
};

export default InputForm;
